import ImagePicker from '../components/ImagePicker';
import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  REGISTER_COMPANY_MUTATION,
  CHECK_SLUG_AVAILABLE_QUERY,
  SEARCH_PLATFORM_USERS_QUERY,
  ADD_COMPANY_ADMIN_MUTATION,
} from '../graphql';

const DRAFT_KEY = 'hc_company_create_draft';
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/;

const INDUSTRIES = [
  'Technology', 'Education', 'Healthcare', 'Finance',
  'Retail', 'Manufacturing', 'Entertainment', 'Nonprofit', 'Other',
];

const SIZE_BANDS = ['1–10', '11–50', '51–200', '201–1000', '1000+'];

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    const d = raw ? JSON.parse(raw) : {};
    return { step: d.step ?? 1, companyId: d.companyId ?? null, slug: d.slug ?? null };
  } catch { return { step: 1, companyId: null, slug: null }; }
}
function saveDraft(step, companyId, slug) {
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ step, companyId, slug }));
}
function clearDraft() { sessionStorage.removeItem(DRAFT_KEY); }

export default function CompanyRegister() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const draft = loadDraft();
  const [step, setStep] = useState(draft.step);
  const [companyId, setCompanyId] = useState(draft.companyId);

  // Step 1 state
  const [form, setForm] = useState({
    name: '', industry: '', description: '',
    hqCity: '', sizeBand: '', website: '',
  });
  const [step1Error, setStep1Error] = useState('');

  // Step 2 state
  const [slug, setSlug] = useState(draft.slug || '');
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | true | false
  const [brandName, setBrandName] = useState('');
  const [brandColor, setBrandColor] = useState('#C05621');
  const [logoUrl, setLogoUrl] = useState('');
  const [tagline, setTagline] = useState('');
  const [step2Error, setStep2Error] = useState('');

  // Step 3 state
  const [adminSearch, setAdminSearch] = useState('');
  const [addedAdmins, setAddedAdmins] = useState([]);
  const [step3Error, setStep3Error] = useState('');

  // Persist draft on change
  useEffect(() => {
    saveDraft(step, companyId, slug || null);
  }, [step, companyId, slug]);

  // GraphQL
  const [registerCompany, { loading: registering }] = useMutation(REGISTER_COMPANY_MUTATION, {
    onCompleted: (data) => {
      const c = data?.registerCompany;
      if (c?.id) {
        setCompanyId(c.id);
        setStep(3);
      }
    },
    onError: (e) => setStep2Error(e.message),
  });

  const [slugUnavailableReason, setSlugUnavailableReason] = useState('');
  const [checkSlug] = useLazyQuery(CHECK_SLUG_AVAILABLE_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      const result = d?.checkSlugAvailable;
      if (result?.available === false && result?.reason) {
        setSlugUnavailableReason(result.reason);
        setSlugStatus(false);
      } else {
        setSlugUnavailableReason('');
        setSlugStatus(result?.available ?? false);
      }
    },
    onError: () => { setSlugStatus(false); setSlugUnavailableReason(''); },
  });

  const [searchUsers, { data: searchData, loading: searching }] = useLazyQuery(
    SEARCH_PLATFORM_USERS_QUERY,
    { fetchPolicy: 'network-only' }
  );

  const [addAdmin, { loading: adding }] = useMutation(ADD_COMPANY_ADMIN_MUTATION, {
    onError: (e) => setStep3Error(e.message),
  });

  // Debounced slug check
  useEffect(() => {
    setSlugStatus(null);
    const s = slug.trim().toLowerCase();
    if (!s || !SLUG_RE.test(s)) return;
    setSlugStatus('checking');
    const t = window.setTimeout(() => {
      checkSlug({ variables: { slug: s } });
    }, 400);
    return () => window.clearTimeout(t);
  }, [slug, checkSlug]);

  const setField = useCallback((k) => (e) =>
    setForm(f => ({ ...f, [k]: e.target.value })), []);

  // Step 1 → 2
  const handleStep1 = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setStep1Error('Company name is required.'); return; }
    setStep1Error('');
    if (!brandName) setBrandName(form.name.trim());
    setStep(2);
  };

  // Step 2 → create company → step 3
  const handleStep2 = () => {
    const s = slug.trim().toLowerCase();
    if (!SLUG_RE.test(s)) {
      setStep2Error('Subdomain must be 3–63 chars: lowercase letters, digits, hyphens — not at the ends.');
      return;
    }
    if (slugStatus === false) { setStep2Error(slugUnavailableReason || 'That subdomain is already taken — try another.'); return; }
    if (slugStatus === 'checking' || slugStatus === null) {
      setStep2Error('Please wait while we check slug availability.'); return;
    }
    setStep2Error('');
    registerCompany({
      variables: {
        name: form.name.trim(),
        slug: s,
        description: form.description.trim() || null,
        industry: form.industry || null,
        sizeBand: form.sizeBand || null,
        logoUrl: logoUrl.trim() || null,
        brandColor,
        tagline: tagline.trim() || null,
        website: form.website.trim() || null,
      },
    });
  };

  // Add admin
  const handleAddAdmin = async (userId) => {
    if (!companyId) return;
    setStep3Error('');
    try {
      await addAdmin({ variables: { companyId, userId } });
      setAddedAdmins(prev => [...prev, userId]);
    } catch (_) { /* error handled in onError */ }
  };

  // Finish
  const handleFinish = () => {
    clearDraft();
    navigate(`/company/${slug.trim().toLowerCase()}/dashboard`);
  };

  const searchResults = searchData?.searchPlatformUsers ?? [];

  const slugDisplay = slug.trim().toLowerCase();
  const slugValid = SLUG_RE.test(slugDisplay);

  if (!currentUser) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <p>Please sign in first.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: 600, paddingTop: 32, paddingBottom: 48 }}>
        <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>
          🏢 Create Company Space
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 24 }}>
          Your branded community on ledxo.com — invite your team, create groups, host events.
        </p>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: step >= s ? 'var(--primary)' : 'var(--border)',
              transition: 'background 0.2s',
            }} />
          ))}
        </div>

        {/* ── STEP 1: Company Info ── */}
        {step === 1 && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 18 }}>
              Step 1 of 3 — Company Information
            </div>
            <form onSubmit={handleStep1}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Company Name <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  className="input"
                  value={form.name}
                  onChange={setField('name')}
                  placeholder="e.g. Acme Hobbyists Inc."
                  required
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Industry
                </label>
                <select className="input" value={form.industry} onChange={setField('industry')}>
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Description
                </label>
                <textarea
                  className="input"
                  style={{ minHeight: 90, resize: 'vertical' }}
                  value={form.description}
                  onChange={setField('description')}
                  placeholder="What does your company do? What community are you building?"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                    HQ City
                  </label>
                  <input
                    className="input"
                    value={form.hqCity}
                    onChange={setField('hqCity')}
                    placeholder="Bengaluru"
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                    Company Size
                  </label>
                  <select className="input" value={form.sizeBand} onChange={setField('sizeBand')}>
                    <option value="">Select size…</option>
                    {SIZE_BANDS.map(b => <option key={b} value={b}>{b} employees</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Website URL
                </label>
                <input
                  className="input"
                  type="url"
                  value={form.website}
                  onChange={setField('website')}
                  placeholder="https://yourcompany.com"
                />
              </div>

              {step1Error && (
                <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>
                  {step1Error}
                </div>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                Continue — Brand Your Space →
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 2: Brand your space ── */}
        {step === 2 && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 6 }}>
              Step 2 of 3 — Brand Your Space
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 20 }}>
              Claim your subdomain and set your brand so members instantly recognise your community.
            </p>

            {/* Subdomain */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                Choose your subdomain <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)', marginBottom: 8 }}>
                Lowercase letters, digits and hyphens only. This is permanent.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  value={slug}
                  onChange={e => setSlug(e.target.value.toLowerCase())}
                  placeholder="yourcompany"
                  autoFocus
                />
                <span style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  .ledxo.com
                </span>
              </div>
              {/* Live URL preview */}
              {slugDisplay && (
                <div style={{
                  marginTop: 8,
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface2)',
                  fontSize: 'var(--font-sm)',
                  fontFamily: 'monospace',
                  color: slugValid ? 'var(--text)' : 'var(--text-muted)',
                }}>
                  {slugValid
                    ? `https://${slugDisplay}.ledxo.com`
                    : `⚠ ${slugDisplay}.ledxo.com — invalid format`}
                </div>
              )}
              {/* Availability feedback */}
              {slugDisplay && slugValid && (
                <p style={{
                  fontSize: 'var(--font-sm)', fontWeight: 600, marginTop: 6,
                  color: slugStatus === 'checking' ? 'var(--text-muted)'
                    : slugStatus === true ? 'var(--success)'
                    : slugStatus === false ? 'var(--danger)'
                    : 'var(--text-muted)',
                }}>
                  {slugStatus === 'checking' ? 'Checking…'
                    : slugStatus === true ? `✓ Available — https://${slugDisplay}.ledxo.com is yours`
                    : slugStatus === false
                      ? `✗ ${slugUnavailableReason || 'Taken — try another subdomain'}`
                    : ''}
                </p>
              )}
            </div>

            {/* Brand name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                Brand Name
              </label>
              <p style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)', marginBottom: 8 }}>
                What members see instead of your legal company name (defaults to company name).
              </p>
              <input
                className="input"
                value={brandName}
                onChange={e => setBrandName(e.target.value)}
                placeholder={form.name || 'Your brand name'}
              />
            </div>

            {/* Brand color + logo side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 16px', marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Brand Color
                </label>
                <input
                  type="color"
                  value={brandColor}
                  onChange={e => setBrandColor(e.target.value)}
                  style={{ width: '100%', height: 42, padding: 4, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Logo <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <ImagePicker
                  value={logoUrl} onChange={setLogoUrl} preset="logo" label=""
                  previewWidth={72} previewHeight={72}
                  hint="Upload your logo, or paste a link to one."
                />
              </div>
            </div>

            {/* Logo preview */}
            {logoUrl && (
              <div style={{ marginBottom: 16 }}>
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  style={{ height: 48, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 4 }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            {/* Tagline */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                Tagline <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(max 80 chars)</span>
              </label>
              <input
                className="input"
                value={tagline}
                maxLength={80}
                onChange={e => setTagline(e.target.value)}
                placeholder="Connect, grow and thrive together."
              />
              <p style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)', marginTop: 4 }}>
                {tagline.length}/80
              </p>
            </div>

            {/* Live brand preview banner */}
            <div style={{
              borderRadius: 'var(--radius)',
              overflow: 'hidden',
              marginBottom: 20,
              border: '1px solid var(--border)',
            }}>
              <div style={{
                background: brandColor,
                padding: '18px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="" style={{ width: 40, height: 40, objectFit: 'contain', background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 3 }} onError={() => {}} />
                ) : (
                  <div style={{ width: 40, height: 40, borderRadius: 6, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, color: '#fff' }}>
                    {(brandName || form.name || 'C')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>{brandName || form.name || 'Your Company'}</div>
                  {tagline && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>{tagline}</div>}
                  {slugDisplay && slugValid && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{slugDisplay}.ledxo.com</div>
                  )}
                </div>
              </div>
              <div style={{ background: 'var(--surface2)', padding: '8px 20px', fontSize: 11, color: 'var(--text-muted)' }}>
                Live preview — how your banner will look to members
              </div>
            </div>

            {step2Error && (
              <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>
                {step2Error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={handleStep2}
                disabled={registering || slugStatus === 'checking'}
              >
                {registering ? 'Creating…' : 'Create Company Space →'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Add Admins ── */}
        {step === 3 && companyId && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{
              padding: '12px 16px', borderRadius: 'var(--radius-sm)',
              background: 'rgba(56,120,90,0.1)', border: '1px solid rgba(56,120,90,0.3)',
              marginBottom: 20,
            }}>
              <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 'var(--font-sm)' }}>
                🎉 Application submitted!
              </div>
              <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginTop: 4 }}>
                Your company space is <strong>pending admin approval</strong>. You'll be notified once approved. Your subdomain{' '}
                <span style={{ color: 'var(--primary)', fontWeight: 700 }}>
                  {slug.trim().toLowerCase()}.ledxo.com
                </span>
                {' '}will be active after approval.
              </div>
            </div>

            <div style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 6 }}>
              Step 3 of 3 — Add Company Admins
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 18 }}>
              Search for platform users by name or email and add them as company admins. You can skip this now.
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="Search by name or email…"
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && adminSearch.trim()) {
                    searchUsers({ variables: { search: adminSearch.trim(), groupId: companyId } });
                  }
                }}
              />
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (adminSearch.trim()) {
                    searchUsers({ variables: { search: adminSearch.trim(), groupId: companyId } });
                  }
                }}
                disabled={searching}
              >
                {searching ? '…' : 'Search'}
              </button>
            </div>

            {step3Error && (
              <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 12 }}>
                {step3Error}
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 16 }}>
                {searchResults.map((u, i) => {
                  const alreadyAdded = addedAdmins.includes(u.id);
                  return (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      borderBottom: i < searchResults.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <div className="avatar avatar-sm" style={{ background: u.avatarColor || 'var(--primary)', flexShrink: 0 }}>
                        {(u.name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>{u.name}</div>
                        <div style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)' }}>{u.email}</div>
                      </div>
                      {alreadyAdded ? (
                        <span style={{ fontSize: 'var(--font-sm)', color: 'var(--success)', fontWeight: 600 }}>✓ Added</span>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleAddAdmin(u.id)}
                          disabled={adding}
                          style={{ border: '1px solid var(--primary)', color: 'var(--primary)', background: 'transparent' }}
                        >
                          + Add admin
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {addedAdmins.length > 0 && (
              <div style={{ fontSize: 'var(--font-sm)', color: 'var(--success)', fontWeight: 600, marginBottom: 16 }}>
                ✓ {addedAdmins.length} admin{addedAdmins.length !== 1 ? 's' : ''} added
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={handleFinish}
              >
                Skip for now
              </button>
              <button
                className="btn btn-primary"
                onClick={handleFinish}
              >
                Go to Dashboard →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
